import { useEffect, type RefObject } from 'react';

/**
 * Ancla clic y hover al nodo de `externalTriggerRef` (sin botón interno en `Popup`).
 */
export function usePopupExternalTrigger(params: {
  externalTriggerRef: RefObject<HTMLElement | SVGElement | null> | undefined;
  /** Si es `false`, no enlaza clic/hover en el ancla externo (solo posicionamiento). */
  triggerRefInteraction: boolean;
  disabled: boolean;
  hoverOpenEnabled: boolean;
  /** `true` cuando `openOnHover` y `hoverAnchorFollowPointer` están activos en `Popup`. */
  hoverAnchorFollowPointer: boolean;
  togglePopup: (event?: MouseEvent) => void;
  onHoverTriggerEnter: (event?: MouseEvent) => void;
  onHoverTriggerLeave: () => void;
  onHoverTriggerPointerMoveNative: (event: MouseEvent) => void;
}): void {
  const {
    externalTriggerRef,
    triggerRefInteraction,
    disabled,
    hoverOpenEnabled,
    hoverAnchorFollowPointer,
    togglePopup,
    onHoverTriggerEnter,
    onHoverTriggerLeave,
    onHoverTriggerPointerMoveNative,
  } = params;

  useEffect(() => {
    if (!externalTriggerRef || !triggerRefInteraction) {
      return;
    }

    let cleaned = false;
    let cleanupListeners: (() => void) | undefined;

    const attach = () => {
      const el = externalTriggerRef.current;
      if (!el || cleaned) {
        return;
      }

      const onNativeClick = (evt: Event) => {
        const e = evt as MouseEvent;
        e.stopPropagation();
        if (disabled) return;
        togglePopup(e);
      };

      el.addEventListener('click', onNativeClick);
      const removers: (() => void)[] = [() => el.removeEventListener('click', onNativeClick)];

      if (hoverOpenEnabled) {
        const onEnter = (evt: Event) => {
          onHoverTriggerEnter(evt as MouseEvent);
        };
        el.addEventListener('mouseenter', onEnter);
        removers.push(() => el.removeEventListener('mouseenter', onEnter));

        el.addEventListener('mouseleave', onHoverTriggerLeave);
        removers.push(() => el.removeEventListener('mouseleave', onHoverTriggerLeave));

        if (hoverAnchorFollowPointer) {
          const onMove = (evt: Event) => {
            onHoverTriggerPointerMoveNative(evt as MouseEvent);
          };
          el.addEventListener('mousemove', onMove, { passive: true });
          removers.push(() => el.removeEventListener('mousemove', onMove));
        }
      }

      cleanupListeners = () => removers.forEach((r) => r());
    };

    const id = window.requestAnimationFrame(() => {
      attach();
    });

    return () => {
      cleaned = true;
      window.cancelAnimationFrame(id);
      cleanupListeners?.();
    };
  }, [
    externalTriggerRef,
    triggerRefInteraction,
    disabled,
    hoverOpenEnabled,
    hoverAnchorFollowPointer,
    togglePopup,
    onHoverTriggerEnter,
    onHoverTriggerLeave,
    onHoverTriggerPointerMoveNative,
  ]);
}
